import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
    ITableColumnSizing,
    ITableFilters,
} from '../../components/Entity/interfaces'
import { fetchColumnsSelections } from '../../services/entityServices'
import { columsnToExcludeInEntityListing } from '../../constants/constants'

export type IAttributeType = 'self' | 'relation'
export interface ColumnOrderListType {
    id: string
    name: string
    selected: boolean
    columnSelection: boolean
    attributeName: string
    columnName: string
    attributeType: IAttributeType | string //Remove this string later by resolving the errors in other places
    dataType: string
    searchable: string
    attributeSourceType?: null | 'Static' | 'Dynamic'
    unit?: string
    relationType?: string | null
}

interface EntityType {
    entityColumns: ColumnOrderListType[]
    allTableFilters: ITableFilters[] // To store filters for different entity tables
    allTableColumnSizing: ITableColumnSizing[]
    scrollPosition: number
    isFetchingColumn: boolean
    columnVisibilityTemplateId: string
    columnVisibilityOrgId: string
}

const initialState: EntityType = {
    entityColumns: [],
    allTableFilters: [],
    allTableColumnSizing: [],
    scrollPosition: 0,
    isFetchingColumn: false,
    columnVisibilityTemplateId: '',
    columnVisibilityOrgId: '',
}

export const getEntityListColumns = createAsyncThunk(
    'entity/getEntityListColumns',
    async (
        payload: {
            orgId: string
            templateId: string
            isAdmin: boolean
            applicationId: string //TO Do: we need to optimize this
            tabIndex: number | string //TO Do: we need to optimize this
            includeParentRelations?: boolean
        },
        { rejectWithValue },
    ) => {
        const {
            orgId,
            templateId,
            isAdmin,
            applicationId,
            tabIndex,
            includeParentRelations,
        } = payload
        try {
            const resColumns = await fetchColumnsSelections(
                templateId,
                orgId,
                includeParentRelations ?? false,
            )

            // Check if any user-defined selections exist.
            const hasSavedSelections = resColumns?.some(
                (col) => col.columnSelection,
            )

            let updatedColumns: ColumnOrderListType[] = (resColumns || [])
                .map((col) => ({
                    ...col,
                    id: col.attributeName,
                    name: col.columnName,
                    // Use saved selection if it exists, otherwise default to true.
                    selected: hasSavedSelections ? col.columnSelection : true,
                }))
                .filter((item: { name: string }) => {
                    // Exclude columns based on the current application and tab context.
                    const exclusions =
                        columsnToExcludeInEntityListing[applicationId || '']?.[
                            Number(tabIndex)
                        ]
                    return !exclusions?.includes(item.name)
                })
            const localStorageColumnsOrder = localStorage.getItem(
                `entityListColumnsOrder_${orgId}_${templateId}`,
            )
            if (localStorageColumnsOrder && !isAdmin) {
                updatedColumns =
                    JSON.parse(localStorageColumnsOrder) || updatedColumns
            }
            return updatedColumns
        } catch (e) {
            return rejectWithValue(e)
        }
    },
)

const entitySlice = createSlice({
    name: 'entity',
    initialState,
    reducers: {
        setFilterList(state, action: PayloadAction<ITableFilters[]>) {
            state.allTableFilters = action.payload
        },
        updateEntityListColumns(
            state,
            action: PayloadAction<ColumnOrderListType[]>,
        ) {
            state.entityColumns = action.payload
        },
        setScrollPosition(state, action: PayloadAction<number>) {
            state.scrollPosition = action.payload
        },
        setTableColumnSizing(
            state,
            action: PayloadAction<{
                id: string
                sizing: Record<string, number>
            }>,
        ) {
            const { id, sizing } = action.payload
            const index = state.allTableColumnSizing.findIndex(
                (item) => item.id === id,
            )
            if (index !== -1) {
                state.allTableColumnSizing[index].sizing = sizing
            } else {
                state.allTableColumnSizing.push({ id, sizing })
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getEntityListColumns.pending, (state) => {
            state.isFetchingColumn = true
        })
        builder.addCase(getEntityListColumns.fulfilled, (state, action) => {
            state.entityColumns = action.payload || []
            state.isFetchingColumn = false
            state.columnVisibilityTemplateId = action.meta.arg.templateId
            state.columnVisibilityOrgId = action.meta.arg.orgId
        })
        builder.addCase(getEntityListColumns.rejected, (state) => {
            state.entityColumns = []
            state.isFetchingColumn = false
            state.columnVisibilityTemplateId = ''
            state.columnVisibilityOrgId = ''
        })
    },
})

export const {
    setFilterList,
    updateEntityListColumns,
    setScrollPosition,
    setTableColumnSizing,
} = entitySlice.actions

export const selectEntityViewScrollPosition = (state: {
    entity: EntityType
}): number => state.entity.scrollPosition

export default entitySlice.reducer
